import type { CSSObject } from '@emotion/react'

import handDrawnBorderBlack from '../../assets/hand-drawn-border-black.svg'

export type ButtonVariant = 'primary' | 'primary-brand' | 'secondary' | 'ghost'
export type ButtonSize = 'small' | 'default' | 'huge'

const BASE: CSSObject = {
  position: 'relative',
  isolation: 'isolate',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-system)',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  border: 'none',
  background: 'transparent',
  '.button-icon': {
    marginRight: 8,
  },
  '.button-chevron': {
    marginLeft: 6,
    transition: 'transform .2s cubic-bezier(.215,.61,.355,1)',
    transform: 'translateX(6px)',
  },
  ':hover .button-chevron, :focus .button-chevron': {
    transform: 'translateX(8px)',
  },
  ':disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}

// The "primary"-family variants paint their background through a ::before pseudo
// so the bg can scale on hover while the button (and its text) stay still.
const primaryPseudoBg = (color: string): CSSObject => ({
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: color,
    borderRadius: 'inherit',
    transition: 'transform .2s cubic-bezier(.215,.61,.355,1)',
    zIndex: -1,
  },
  '&:hover:not(:disabled)::before, &.active::before': {
    transform: 'scale(1.02)',
  },
})

const VARIANTS: Record<ButtonVariant, CSSObject> = {
  primary: {
    color: 'var(--shade-100)',
    ...primaryPseudoBg('var(--shade-00)'),
    background: 'var(--shade-00)',
  },
  'primary-brand': {
    color: 'var(--shade-100)',
    ...primaryPseudoBg('var(--saturn-orange)'),
    background: 'var(--saturn-orange)',
  },
  secondary: {
    color: 'var(--shade-00)',
    border: '2px solid transparent',
    borderImage: `url("${handDrawnBorderBlack}") 20 / 20px / 0px`,
    ':hover:not(:disabled), &.active': {
      backgroundColor: 'var(--shade-100)',
    },
  },
  ghost: {
    color: 'var(--shade-05)',
    ':hover:not(:disabled), &.active': {
      backgroundColor: 'var(--moon-dust)',
      color: 'var(--shade-00)',
    },
  },
}

const SIZES: Record<ButtonSize, CSSObject> = {
  small: {
    height: 32,
    padding: '0 10px',
    fontSize: 15,
  },
  default: {
    height: 38,
    padding: '0 16px',
    fontSize: 16,
  },
  huge: {
    height: 54,
    padding: '0 24px',
    fontSize: 17,
  },
}

export function buttonCss(variant: ButtonVariant, size: ButtonSize): CSSObject {
  return {
    ...BASE,
    ...SIZES[size],
    ...VARIANTS[variant],
  }
}
